/**
 * Responsive context (TASK-220, PRD §13.5).
 *
 * Single source of truth for viewport class in the admin SPA. Backed by
 * Mantine's `useMediaQuery` so the breakpoints stay in sync with the design
 * tokens shipped in `@mantine/core`.
 *
 * Breakpoints:
 *   - mobile   ≤ 768px            → read-only per CEO decision 2026-05-14
 *   - tablet   769–1024px         → full functionality (iPad portrait/landscape)
 *   - desktop  > 1024px           → full functionality
 *
 * `readOnly` is an alias for `isMobile`: the only mode that gates mutating UI
 * at the UI layer. Backend authorisation is unchanged (RLS at DB level).
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMediaQuery } from '@mantine/hooks';

export interface ResponsiveContextValue {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  readOnly: boolean;
}

const ResponsiveContext = createContext<ResponsiveContextValue | null>(null);

export function ResponsiveProvider({ children }: { children: ReactNode }) {
  const isMobile = useMediaQuery('(max-width: 768px)') ?? false;
  const isTablet =
    useMediaQuery('(min-width: 769px) and (max-width: 1024px)') ?? false;
  const isDesktop = !isMobile && !isTablet;

  const value = useMemo<ResponsiveContextValue>(
    () => ({ isMobile, isTablet, isDesktop, readOnly: isMobile }),
    [isMobile, isTablet, isDesktop],
  );

  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  );
}

export function useResponsive(): ResponsiveContextValue {
  const ctx = useContext(ResponsiveContext);
  if (!ctx) {
    throw new Error('useResponsive must be used inside <ResponsiveProvider>');
  }
  return ctx;
}
